import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Clock,
  GasPump,
  Globe,
  LinkBreak,
  ListBullets,
  MagnifyingGlass,
  MapPin,
  PhoneOutgoing,
  Sparkle,
  Ticket,
  Tire,
  Truck,
  Warning,
  Wrench,
  X,
  XCircle,
} from '@phosphor-icons/react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Phone } from '@phosphor-icons/react/dist/ssr';
import { useJsApiLoader } from '@react-google-maps/api';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LOADER_OPTIONS,
} from '../../../../../utils/googleMapsLoader';
import {
  APIProvider,
  Map,
  Marker,
  type MapEvent,
} from '@vis.gl/react-google-maps';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import VendorFilterButtons from './widgets/VendorFilterButtons';
import {
  CustomerWithDriverOnboarding,
  VendorBusinessLocationWithDeals,
  VendorLocationPin,
} from '../../../../../types/apiTypes';
import SelectedLocationDetails from './widgets/SelectedLocationDetails';
import { HttpStatusCode } from 'axios';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import dayjs from 'dayjs';
import { ClipLoader } from 'react-spinners';
import { Circle } from './widgets/MapCircle';
import DraggableSearchResults from './widgets/DraggableSearchResults';
import SearchThisAreaButton from './widgets/SearchThisAreaButton';
import RecenterButton from './widgets/RecenterButton';
import {
  sanitizePhoneNumber,
  sanitizeUrl,
} from '../../../../../utils/SanitizationUtils';
import { useRef } from 'react';
import SupportModal from '../SupportModal';
import { isPreActiveCustomer } from '../home/utils/demoData';
import { safeGetItem, safeSetItem } from '../../../../../shared/safeStorage';

interface VendorMarketplaceProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

interface DataPoint {
  position: [number, number];
  icon: ReactElement;
  color: [number, number, number];
  name: string;
  address: string;
  googleMapsUrl: string;
  id: string;
  primary_vendor_type: string;
  secondary_vendor_types: string[];
}

// How far the map must be panned, as a fraction of the selected search radius,
// before "Search this area" is offered (2.5 miles at the default 10 mile
// radius). Doubles as the tolerance for panning back: return inside this band
// and the button goes away again.
const SEARCH_AREA_MOVE_THRESHOLD_RATIO = 0.25;

const mapStyles = [
  {
    featureType: 'administrative.province',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'administrative.land_parcel',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#e9e5dc',
      },
    ],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
      {
        color: '#b8cb93',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'poi.attraction',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.government',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.government',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    elementType: 'geometry.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ccdca1',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.school',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [
      {
        hue: '#ff0000',
      },
      {
        saturation: -100,
      },
      {
        lightness: 99,
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#808080',
      },
      {
        lightness: 54,
      },
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#767676',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'transit.station.airport',
    elementType: 'labels.text.fill',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'transit.station.airport',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        saturation: 43,
      },
      {
        lightness: -11,
      },
      {
        color: '#89cada',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        visibility: 'on',
      },
    ],
  },
];

function VendorMarketplace({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: VendorMarketplaceProps) {
  const [viewState, setViewState] = useState({
    longitude: -84.55419067740269,
    latitude: 33.76083759670232,
    zoom: 10,
    pitch: 0,
    bearing: 0,
  });
  const { screenView } = useScreenView();
  const [userLocation, setUserLocation] =
    useState<google.maps.LatLngLiteral | null>(null);
  // Where the last search actually ran. Seeded from userLocation, then owned by
  // the user once they pan and hit "Search this area".
  const [searchCenter, setSearchCenter] =
    useState<google.maps.LatLngLiteral | null>(null);
  // Live map camera center, updated when the map settles after a pan/zoom.
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(
    null
  );
  // True once the user has taken the map somewhere themselves, by dragging it
  // or by searching an area. Until then the map follows geolocation; after, a
  // late location update can't move the viewport or search out from under them.
  const hasUserTakenControlRef = useRef(false);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  // A center to pan to as soon as the map exists. Geolocation can resolve
  // before the map's first idle, which is where we get the map handle, so the
  // pan has to wait rather than being dropped.
  const pendingCenterRef = useRef<google.maps.LatLngLiteral | null>(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const [distances, setDistances] = useState<{ [key: string]: string }>({});
  const [openRadiusSelect, setOpenRadiusSelect] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState(10);
  const toggleOpenRadiusSelect = () => setOpenRadiusSelect(!openRadiusSelect);
  const [filterServiceCenter, setFilterServiceCenter] =
    useState<boolean>(false);
  const [filterGasStation, setFilterGasStation] = useState<boolean>(false);
  const [filterTowing, setFilterTowing] = useState<boolean>(false);
  const [filterTires, setFilterTires] = useState<boolean>(false);
  const [filterRental, setFilterRental] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [selectedVendorLocation, setSelectedVendorLocation] =
    useState<VendorBusinessLocationWithDeals | null>(null);
  const [vendorLocations, setVendorLocations] = useState<
    VendorBusinessLocationWithDeals[]
  >([]);
  const [pins, setPins] = useState<VendorLocationPin[]>([]);
  const [cardOffset, setCardOffset] = useState<number>(0);
  const [hasMoreCards, setHasMoreCards] = useState<boolean>(false);
  const [loadingMoreCards, setLoadingMoreCards] = useState<boolean>(false);
  const PAGE_SIZE = 25;
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchingMoreRef = useRef(false);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  const [isActive, setIsActive] = useState(false);

  const [isSupportModalOpen, setIsSupportModalOpen] = useState<boolean>(false);
  const handleMarkerClick = useCallback(
    (id: string) => {
      setSelectedVendorLocation(
        vendorLocations.find(
          (vendorLocation) => vendorLocation?.location_id === id
        ) || null
      );
    },
    [vendorLocations]
  );
  const [loadingVendorLocations, setLoadingVendorLocations] =
    useState<boolean>(true);
  const { userId, userRecord } = useUser();
  const { callProtectedApi } = useApi();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  useEffect(() => {
    // Find the camera control button
    const findButton = () =>
      document.querySelector(
        'gmp-internal-camera-control button[aria-label="Map camera controls"]'
      ) as HTMLElement | null;

    let button = findButton();
    let observer: MutationObserver | null = null;

    const handleChange = () => {
      if (!button) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      setIsActive(expanded);
    };

    // If the button exists now
    if (button) {
      // Initial state
      handleChange();

      // Watch for aria-expanded attribute changes
      observer = new MutationObserver(() => handleChange());
      observer.observe(button, {
        attributes: true,
        attributeFilter: ['aria-expanded'],
      });

      // Also listen for clicks in case Maps rebuilds the control
      button.addEventListener('click', handleChange);
    }

    // If it doesn’t exist yet, retry for a few seconds
    const retryInterval = setInterval(() => {
      if (!button) {
        button = findButton();
        if (button) {
          handleChange();
          observer = new MutationObserver(() => handleChange());
          observer.observe(button, {
            attributes: true,
            attributeFilter: ['aria-expanded'],
          });
          button.addEventListener('click', handleChange);
          clearInterval(retryInterval);
        }
      }
    }, 500);

    return () => {
      clearInterval(retryInterval);
      observer?.disconnect();
      button?.removeEventListener('click', handleChange);
    };
  }, []);

  useEffect(() => {
    try {
      let userLoc = JSON.parse(safeGetItem('tachUserLocation') ?? '');
      setUserLocation({
        lat: userLoc.latitude,
        lng: userLoc.longitude,
      });
      setViewState({
        ...viewState,
        latitude: userLoc.latitude,
        longitude: userLoc.longitude,
      });
    } catch (e: any) {
      console.error('Error fetching user location:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery]);

  const activeVendorTypes = useMemo(() => {
    const types: string[] = [];
    if (filterGasStation) types.push('fuel_station');
    if (filterServiceCenter) types.push('repair_shop');
    if (filterRental) types.push('truck_rental');
    if (filterTowing) types.push('towing_service');
    if (filterTires) types.push('tire_supplier');
    return types;
  }, [
    filterGasStation,
    filterServiceCenter,
    filterRental,
    filterTowing,
    filterTires,
  ]);

  const fetchVendorPins = useCallback(
    async (customer_id: string) => {
      try {
        const response = await callProtectedApi(
          `/vendorBusinessLocations/radius/${customer_id}/pins`,
          'post',
          {
            radius: selectedRadius,
            latitude: searchCenter?.lat,
            longitude: searchCenter?.lng,
          },
          'Error occurred while fetching vendor map pins.'
        );
        const data = response?.data?.data?.pins;
        if (data && response?.status === HttpStatusCode.Ok) {
          setPins(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching vendor map pins: ', e);
      }
    },
    [callProtectedApi, selectedRadius, searchCenter?.lat, searchCenter?.lng]
  );

  const fetchVendorCards = useCallback(
    async (customer_id: string, offset: number, append: boolean) => {
      if (append && fetchingMoreRef.current) return;
      try {
        if (append) {
          setLoadingMoreCards(true);
          fetchingMoreRef.current = true;
        } else {
          fetchingMoreRef.current = false;
        }
        const response = await callProtectedApi(
          `/vendorBusinessLocations/radius/${customer_id}`,
          'post',
          {
            radius: selectedRadius,
            latitude: searchCenter?.lat,
            longitude: searchCenter?.lng,
            limit: PAGE_SIZE,
            offset,
            ...(debouncedSearchQuery
              ? { searchQuery: debouncedSearchQuery }
              : {}),
            ...(activeVendorTypes.length
              ? { vendorTypes: activeVendorTypes }
              : {}),
          },
          'Error occurred while fetching vendor locations.'
        );
        const data = response?.data?.data?.vendorBusinessLocations;
        const more = response?.data?.data?.hasMore ?? false;
        if (data && response?.status === HttpStatusCode.Ok) {
          setVendorLocations((prev) => (append ? [...prev, ...data] : data));
          setHasMoreCards(more);
          setCardOffset(offset + data.length);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching vendor locations: ', e);
      } finally {
        setLoadingVendorLocations(false);
        setLoadingMoreCards(false);
        fetchingMoreRef.current = false;
      }
    },
    [
      callProtectedApi,
      selectedRadius,
      searchCenter?.lat,
      searchCenter?.lng,
      debouncedSearchQuery,
      activeVendorTypes,
    ]
  );

  // Follow the user's location until they take control of the map. The map is
  // uncontrolled, so its viewport is pinned to whichever location arrived first
  // — usually a stale localStorage entry. Panning it to match keeps the
  // viewport, the radius ring and the search center on the same city, and stops
  // "Search this area" from appearing on load pointing at the stale one.
  useEffect(() => {
    if (hasUserTakenControlRef.current || !userLocation) return;
    setSearchCenter(userLocation);
    setMapCenter(userLocation);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(userLocation);
    } else {
      // No map yet — hand the pan to the first idle instead of losing it and
      // leaving the viewport on the stale city the search just moved off.
      pendingCenterRef.current = userLocation;
    }
  }, [userLocation]);

  useEffect(() => {
    if (searchCenter?.lat != null && searchCenter?.lng != null) {
      const customer_id = userId ?? '';
      setLoadingVendorLocations(true);
      setCardOffset(0);
      fetchVendorPins(customer_id);
      fetchVendorCards(customer_id, 0, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCenter, selectedRadius, debouncedSearchQuery, activeVendorTypes]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        safeSetItem('tachUserLocation', JSON.stringify(position.coords));
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setViewState({
          ...viewState,
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
        });
      },
      (error) => {
        console.error('Error fetching user location:', error);
      },
      {
        enableHighAccuracy: false,
        maximumAge: 10000,
      }
    );
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const filteredPins = useMemo(() => {
    let result = pins;
    if (activeVendorTypes.length) {
      result = result.filter((p) =>
        activeVendorTypes.includes(p.primary_vendor_type)
      );
    }
    if (debouncedSearchQuery) {
      const q = debouncedSearchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.business_name?.toLowerCase().includes(q) ||
          p.primary_vendor_type?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [pins, activeVendorTypes, debouncedSearchQuery]);

  const markerPoints: DataPoint[] = useMemo(() => {
    return filteredPins
      .map((pin): DataPoint | null => {
        if (!pin.longitude || !pin.latitude) return null;
        return {
          id: pin.location_id,
          position: [Number(pin.longitude), Number(pin.latitude)] as [
            number,
            number,
          ],
          icon: (() => {
            switch (pin.primary_vendor_type) {
              case 'repair_shop':
                return <Wrench size={30} className="text-white" />;
              case 'fuel_station':
                return <GasPump size={30} className="text-white" />;
              case 'truck_rental':
                return <Truck size={30} className="text-white" />;
              case 'towing_service':
                return <LinkBreak size={30} className="text-white" />;
              case 'tire_supplier':
                return <Tire size={30} className="text-white" />;
              default:
                return <MapPin size={30} className="text-white" />;
            }
          })(),
          color: [233, 151, 48] as [number, number, number],
          name: pin.business_name ?? '',
          address: '',
          googleMapsUrl: '',
          primary_vendor_type: pin.primary_vendor_type ?? '',
          secondary_vendor_types: [],
        };
      })
      .filter((p): p is DataPoint => !!p);
  }, [filteredPins]);

  useEffect(() => {
    if (userLocation && vendorLocations.length > 0) {
      const newDistances: { [key: string]: string } = {};
      vendorLocations.forEach((loc) => {
        if (loc?.latitude && loc?.longitude) {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            Number(loc?.latitude),
            Number(loc?.longitude)
          );
          newDistances[loc?.location_id] = `${distance.toFixed(1)} mi`;
        }
      });
      setDistances(newDistances);
    }
  }, [userLocation, vendorLocations]);

  function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 3959; // Radius of the Earth in miles
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // The map is uncontrolled, so React only learns where it is by listening.
  // onIdle fires once the camera settles, rather than on every frame of a drag.
  const handleMapIdle = useCallback((event: MapEvent) => {
    // Also the only handle we get on the map itself, which recentering needs.
    mapInstanceRef.current = event.map;
    const pending = pendingCenterRef.current;
    if (pending) {
      // A location landed before the map did. Move there now; the idle this
      // pan fires reports the corrected center.
      pendingCenterRef.current = null;
      event.map.panTo(pending);
      return;
    }
    const center = event.map.getCenter()?.toJSON();
    if (center) setMapCenter(center);
  }, []);

  // Only user drags reach this — panTo and animated recentering don't.
  const handleMapDragStart = useCallback(() => {
    hasUserTakenControlRef.current = true;
  }, []);

  const handleSearchThisArea = useCallback(() => {
    if (!mapCenter || isPreActive) return;
    hasUserTakenControlRef.current = true;
    setSelectedVendorLocation(null);
    setSearchCenter(mapCenter);
  }, [mapCenter, isPreActive]);

  // Pan back to the user and hand the search center back to geolocation. If
  // they never moved, userLocation is already the searchCenter object, so this
  // pans without re-running the search.
  const handleRecenter = useCallback(() => {
    if (!userLocation) return;
    hasUserTakenControlRef.current = false;
    mapInstanceRef.current?.panTo(userLocation);
    setMapCenter(userLocation);
    setSelectedVendorLocation(null);
    setSearchCenter(userLocation);
  }, [userLocation]);

  // Only offer to re-search once they've panned a meaningful fraction of the
  // current radius, so the button doesn't flicker in on incidental drags.
  const milesFromSearchCenter =
    mapCenter && searchCenter
      ? calculateDistance(
          searchCenter.lat,
          searchCenter.lng,
          mapCenter.lat,
          mapCenter.lng
        )
      : 0;
  const canSearchThisArea =
    milesFromSearchCenter > selectedRadius * SEARCH_AREA_MOVE_THRESHOLD_RATIO;

  const generateCustomMarkerIcon = (
    iconComponent: ReactElement,
    color: string,
    vendorName?: string
  ): string => {
    // Check if it's Penske or Ryder and create custom markers with their brand colors
    if (vendorName === 'Penske Truck Rental') {
      // For Penske, create a custom marker SVG with space for logo
      const markerSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="55" viewBox="0 0 40 55">
          <!-- White circular background with blue border -->
          <circle cx="20" cy="20" r="19" fill="white" stroke="#003d82" stroke-width="2"/>
          
          <!-- Blue triangle pointer -->
          <polygon points="10,37 30,37 20,50" fill="#003d82" />
          
          <!-- Penske text (as placeholder for logo) -->
          <text x="20" y="25" font-family="Arial, sans-serif" font-size="10" font-weight="bold" text-anchor="middle" fill="#003d82">PENSKE</text>
        </svg>
      `;

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;
    }

    if (vendorName === 'Ryder Truck Rental') {
      // For Ryder, create a custom marker with their brand colors
      const markerSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="55" viewBox="0 0 40 55">
          <!-- White circular background with red border -->
          <circle cx="20" cy="20" r="19" fill="white" stroke="#FF0000" stroke-width="2"/>
          
          <!-- Red triangle pointer -->
          <polygon points="10,37 30,37 20,50" fill="#FF0000" />
          
          <!-- Ryder text (as placeholder for logo) -->
          <text x="20" y="25" font-family="Arial, sans-serif" font-size="10" font-weight="bold" text-anchor="middle" fill="#FF0000">RYDER</text>
        </svg>
      `;

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;
    }

    if (vendorName === 'Bridgestone Tires') {
      const markerSvg = `
      <?xml version="1.0" encoding="UTF-8"?>
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="250" height="250">
      <path d="M0 0 C9.39128212 -0.52991561 17.36212847 -0.30567126 25.06640625 5.578125 C30.0163565 10.16638786 32.62050433 14.95454722 33.46875 21.76171875 C33.56359265 33.67632711 31.43270037 43.41609531 22.875 52.32421875 C17.03368512 57.54489392 10.28939892 60.32622458 3 63 C3.51949219 63.24234375 4.03898437 63.4846875 4.57421875 63.734375 C12.33701393 67.46510836 17.81190328 71.44317885 21.0859375 79.7578125 C24.235558 90.92954333 21.90283525 101.75449731 17 112 C10.47142863 123.32845061 0.4173232 129.6229086 -11.87109375 133.08886719 C-22.34961633 135.7923437 -32.81348488 135.5028956 -43.5625 135.390625 C-45.69197045 135.38314595 -47.82144788 135.37746091 -49.95092773 135.37347412 C-55.50101401 135.35834868 -61.05066807 135.3191827 -66.60058594 135.2746582 C-72.28574785 135.23341996 -77.97098098 135.21540267 -83.65625 135.1953125 C-94.77102113 135.15267334 -105.88547434 135.08458456 -117 135 C-116.16916162 131.071173 -114.5976197 128.9686929 -112 125.875 C-111.18789062 124.90691406 -110.37578125 123.93882812 -109.5390625 122.94140625 C-106.51938941 119.44323111 -103.45397411 115.99275712 -100.3359375 112.58203125 C-98.41536988 110.45912959 -96.52738194 108.31573129 -94.6484375 106.15625 C-85.52333417 95.70857959 -75.8528306 85.75946798 -66.0625 75.9375 C-65.28229492 75.15270264 -64.50208984 74.36790527 -63.69824219 73.55932617 C-59.52440634 69.39662412 -55.26568932 65.4009297 -50.79711914 61.55639648 C-47.50991054 58.70950682 -44.36682944 55.70130429 -41.19921875 52.72265625 C-39 51 -39 51 -36 51 C-36.62946861 58.28776081 -38.55615852 65.02351593 -40.59765625 72.03125 C-40.95137802 73.26290894 -41.30509979 74.49456787 -41.66954041 75.7635498 C-42.60297108 79.0135024 -43.54352673 82.26134933 -44.48565674 85.50878906 C-45.44858373 88.83272622 -46.40465373 92.15863399 -47.36132812 95.484375 C-49.23461644 101.99140458 -51.11468734 108.49644475 -53 115 C-50.74173983 114.8722661 -48.48401518 114.73503822 -46.2265625 114.59375 C-44.96924316 114.51833984 -43.71192383 114.44292969 -42.41650391 114.36523438 C-38.12270979 113.90621508 -35.99250036 113.15000038 -33 110 C-29.4075971 103.45134424 -27.48629967 96.29168749 -25.53125 89.12890625 C-25.21390396 87.98881104 -24.89655792 86.84871582 -24.56959534 85.67407227 C-23.89129083 83.23432667 -23.2160868 80.79371729 -22.54373169 78.35232544 C-21.31412766 73.88759117 -20.07310074 69.42608556 -18.83124828 64.96474552 C-18.65354051 64.32628413 -18.47583273 63.68782274 -18.29273987 63.03001404 C-18.11492037 62.39118223 -17.93710087 61.75235043 -17.7538929 61.09416008 C-17.21426131 59.15415608 -16.67552951 57.21390436 -16.1370697 55.27357483 C-13.0812159 44.26733689 -9.97953425 33.27463302 -6.84179688 22.29147339 C-6.07355239 19.60120371 -5.30821103 16.91011906 -4.54296875 14.21899414 C-4.0822512 12.60556869 -3.62132705 10.99220221 -3.16015625 9.37890625 C-2.85036583 8.28775513 -2.85036583 8.28775513 -2.53431702 7.17456055 C-1.11485921 2.22971842 -1.11485921 2.22971842 0 0 Z " fill="#231F20" transform="translate(167,57)"/>
      <path d="M0 0 C1.26085419 0.00022659 2.52170837 0.00045319 3.82077026 0.00068665 C5.1814612 0.01600294 6.54214845 0.03165087 7.90283203 0.04760742 C9.30672755 0.05320879 10.71062897 0.05747634 12.11453247 0.06047058 C15.7938684 0.0718769 19.47284411 0.10130699 23.15203857 0.13458252 C26.91267017 0.16537488 30.67336047 0.17899541 34.43408203 0.1940918 C41.80143303 0.22618143 49.16848814 0.27732585 56.53564453 0.34057617 C55.07603976 3.43671873 53.97167678 5.04855081 51.11376953 6.96948242 C50.42508789 7.44135986 49.73640625 7.9132373 49.02685547 8.39941406 C48.28725586 8.89562256 47.54765625 9.39183105 46.78564453 9.90307617 C35.18488703 17.89054324 24.30307608 26.58701597 13.60595703 35.73901367 C10.85830408 38.08315318 8.04764225 40.33940934 5.22314453 42.59057617 C-1.64708041 48.18261973 -7.99940208 54.32204301 -14.42138672 60.4152832 C-16.43255478 62.31060722 -18.45988272 64.18651887 -20.49560547 66.05541992 C-21.13264404 66.64097656 -21.76968262 67.2265332 -22.42602539 67.82983398 C-23.62708597 68.93187559 -24.83025393 70.03162652 -26.03588867 71.12866211 C-28.30090716 73.21279018 -30.36255375 75.19898987 -32.16357422 77.70385742 C-32.59283203 78.24397461 -33.02208984 78.7840918 -33.46435547 79.34057617 C-34.45435547 79.34057617 -35.44435547 79.34057617 -36.46435547 79.34057617 C-35.81388034 71.88410755 -33.86744707 64.91005123 -31.85888672 57.71557617 C-31.59016534 56.74862823 -31.59016534 56.74862823 -31.31601524 55.762146 C-30.36874269 52.35355747 -29.41493902 48.9468812 -28.45532227 45.54174805 C-27.47244201 42.04954624 -26.51118517 38.55177979 -25.55490589 35.05221558 C-24.80904102 32.33777339 -24.04749955 29.62790585 -23.28177452 26.91900635 C-22.74698784 25.0122867 -22.22848432 23.10102372 -21.71040344 21.18969727 C-19.41809691 13.19067507 -17.06605287 6.12314592 -9.67411804 1.65336609 C-6.17640835 0.22280607 -3.76307765 -0.02266914 0 0 Z " fill="#EE2E24" transform="translate(104.46435546875,56.659423828125)"/>
      </svg>
      `;

      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(markerSvg)}`;
    }

    // Default behavior for other vendors
    const iconSvg = renderToStaticMarkup(
      React.cloneElement(iconComponent as ReactElement<any>, {
        color: 'white', // Ensure the icon is white
        size: 24, // Set icon size explicitly
      })
    );

    const markerSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="55" viewBox="0 0 40 55">
          <!-- Circular background -->
          <circle cx="20" cy="20" r="20" fill="${color}" />

          <!-- Triangle pointer connected to the circle, with no gap -->
          <polygon points="10,37 30,37 20,50" fill="${color}" />

          <!-- The icon positioned in the center -->
          <g transform="translate(8, 8)">
            ${iconSvg}
          </g>
        </svg>
      `;

    // Convert the SVG string to a base64-encoded URL
    const markerIconDataUrl = `data:image/svg+xml;base64,${btoa(markerSvg)}`;
    return markerIconDataUrl;
  };

  const openDirectionsWindow = (
    selectedVendorLoc: VendorBusinessLocationWithDeals
  ) => {
    const address =
      selectedVendorLoc?.address_st_number +
      ' ' +
      selectedVendorLoc?.address_st_name +
      (selectedVendorLoc?.address_unit
        ? ' ' + selectedVendorLoc?.address_unit
        : '') +
      ' ' +
      selectedVendorLoc?.address_city +
      ' ' +
      selectedVendorLoc?.address_state +
      ' ' +
      selectedVendorLoc?.address_zip;
    if (navigator.geolocation !== null) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const origin = `${position.coords.latitude},${position.coords.longitude}`;
          const destination = encodeURIComponent(address);
          const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`;
          window.open(url, '_blank');
        },
        (error) => {
          console.error('Error getting current location:', error);
          // Fallback to just opening the destination if geolocation fails
          const destination = encodeURIComponent(address);
          const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
          window.open(url, '_blank');
        }
      );
    } else {
      // Fallback for browsers that don't support geolocation
      const destination = encodeURIComponent(address);
      const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
      window.open(url, '_blank');
    }
  };

  const updateFilterServiceCenter = (filter: boolean) => {
    setFilterServiceCenter(filter);
  };
  const updateFilterTires = (filter: boolean) => {
    setFilterTires(filter);
  };

  const updateFilterTowing = (filter: boolean) => {
    setFilterTowing(filter);
  };

  const updateFilterRental = (filter: boolean) => {
    setFilterRental(filter);
  };

  const updateFilterGasStation = (filter: boolean) => {
    setFilterGasStation(filter);
  };

  const updateSelectedVendorLocation = (
    vendorLocation: VendorBusinessLocationWithDeals | null
  ) => {
    setSelectedVendorLocation(vendorLocation);
  };

  const handleListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (
        hasMoreCards &&
        !loadingMoreCards &&
        el.scrollHeight - el.scrollTop - el.clientHeight < 200
      ) {
        fetchVendorCards(userId ?? '', cardOffset, true);
      }
    },
    [hasMoreCards, loadingMoreCards, fetchVendorCards, userId, cardOffset]
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <div className="flex h-full w-[1148px] max-w-[1148px] flex-col">
              <div className="relative h-full max-h-full min-h-[700px] w-full pt-4">
                {isLoaded && userLocation !== null ? (
                  <>
                    <div className="absolute left-6 top-6 z-10 h-[700px] min-h-[700px] w-[377px] rounded-md bg-primarybg bg-opacity-80 p-[8px]">
                      <div className="relative flex items-center rounded-full bg-white p-3">
                        <MagnifyingGlass size={24} />
                        <input
                          type="text"
                          className="ml-2 w-full border-none outline-none"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                          <XCircle
                            size={24}
                            className="cursor-pointer"
                            onClick={() => setSearchQuery('')}
                          />
                        )}
                      </div>
                      <div className="my-[12px] h-[600px] max-h-[600px]">
                        {!loadingVendorLocations ? (
                          <div
                            className="h-full overflow-y-auto"
                            onScroll={handleListScroll}
                          >
                            {vendorLocations.map((loc, index) => (
                              <VendorLocationRow
                                key={loc?.location_id ?? index}
                                index={index}
                                data={{
                                  filteredVendorLocations: vendorLocations,
                                  selectedVendorLocation,
                                  setSelectedVendorLocation,
                                  distances,
                                  sanitizeUrl,
                                  sanitizePhoneNumber,
                                  hoursOfOperationStringify,
                                }}
                              />
                            ))}
                            {loadingMoreCards && (
                              <div className="flex w-full justify-center py-3">
                                <ClipLoader
                                  color="#1D213B"
                                  size={28}
                                  loading
                                  aria-label="Loading more vendors"
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingVendorLocations}
                              size={60}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="flex h-full w-full justify-center"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {!loadingVendorLocations && (
                      <VendorFilterButtons
                        setFilterTires={updateFilterTires}
                        setFilterRental={updateFilterRental}
                        setFilterTowing={updateFilterTowing}
                        setFilterGasStation={updateFilterGasStation}
                        setFilterServiceCenter={updateFilterServiceCenter}
                        filterTires={filterTires}
                        filterGasStation={filterGasStation}
                        filterRental={filterRental}
                        filterTowing={filterTowing}
                        filterServiceCenter={filterServiceCenter}
                        selectedRadius={selectedRadius}
                        setSelectedRadius={setSelectedRadius}
                        openRadiusSelect={openRadiusSelect}
                        setOpenRadiusSelect={setOpenRadiusSelect}
                        toggleOpenRadiusSelect={toggleOpenRadiusSelect}
                        isCompassExpandable={isActive}
                      />
                    )}
                    {selectedVendorLocation &&
                      selectedVendorLocation?.location_id?.length > 0 &&
                      !loadingVendorLocations && (
                        <>
                          <SelectedLocationDetails
                            distances={distances}
                            openDirectionsWindow={openDirectionsWindow}
                            selectedVendorLocation={selectedVendorLocation}
                            setSelectedVendorLocation={
                              updateSelectedVendorLocation
                            }
                          />
                        </>
                      )}

                    {canSearchThisArea && (
                      <SearchThisAreaButton
                        onClick={handleSearchThisArea}
                        isSearching={loadingVendorLocations}
                        isPreActive={isPreActive}
                      />
                    )}

                    <RecenterButton onClick={handleRecenter} />

                    {userLocation !== null && (
                      <Map
                        mapTypeControl={false}
                        streetViewControl={false}
                        fullscreenControl={false}
                        defaultCenter={{
                          lat: userLocation.lat,
                          lng: userLocation.lng,
                        }}
                        defaultZoom={10}
                        styles={mapStyles}
                        onIdle={handleMapIdle}
                        onDragstart={handleMapDragStart}
                      >
                        {isLoaded && searchCenter !== null && (
                          <Circle
                            center={{
                              lat: searchCenter.lat,
                              lng: searchCenter.lng,
                            }}
                            radius={selectedRadius * 1609.34} // Convert miles to meters
                            strokeColor={'#0c4cb3'}
                            strokeOpacity={0.8}
                            strokeWeight={3}
                            fillColor={'#3b82f6'}
                            fillOpacity={0.15}
                          />
                        )}
                        {isLoaded &&
                          markerPoints.map((point) => (
                            <Marker
                              key={point.id}
                              position={{
                                lat: point.position[1],
                                lng: point.position[0],
                              }}
                              onClick={() => handleMarkerClick(point.id)}
                              icon={(() => {
                                // Special handling for Penske and Ryder to use actual logo files
                                if (point.name === 'Penske Truck Rental') {
                                  return {
                                    url: '/penske-p.png',
                                    scaledSize: new google.maps.Size(50, 50),
                                    anchor: new google.maps.Point(25, 50),
                                  };
                                } else if (
                                  point.name === 'Ryder Truck Rental'
                                ) {
                                  return {
                                    url: '/ryder-r.svg',
                                    scaledSize: new google.maps.Size(40, 40),
                                    anchor: new google.maps.Point(25, 50),
                                  };
                                } else if (point.name === 'Bridgestone Tires') {
                                  return {
                                    url: '/bridgestone-b.svg',
                                    scaledSize: new google.maps.Size(40, 40),
                                    anchor: new google.maps.Point(25, 50),
                                  };
                                } else {
                                  // Default marker for other vendors
                                  return {
                                    url: generateCustomMarkerIcon(
                                      point.icon,
                                      renderIconBg(point.primary_vendor_type),
                                      point.name
                                    ),
                                    scaledSize: new google.maps.Size(40, 40),
                                  };
                                }
                              })()}
                            />
                          ))}
                      </Map>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={loadingVendorLocations}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </APIProvider>
        );
      case 'tablet':
        return (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <div className="flex h-full w-full flex-col">
              <div className="relative h-full w-full">
                {isLoaded && userLocation !== null ? (
                  <>
                    <div
                      className={`absolute left-6 top-6 z-10 h-[64px] max-h-[64px] w-[361px] rounded-md bg-primarybg bg-opacity-80 p-[6px]`}
                    >
                      <div className="relative flex items-center rounded-full bg-white p-3">
                        <MagnifyingGlass size={24} />
                        <input
                          type="text"
                          className="ml-2 w-full border-none outline-none"
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                          <XCircle
                            size={24}
                            className="cursor-pointer"
                            onClick={() => setSearchQuery('')}
                          />
                        )}
                      </div>
                    </div>
                    {showSearchResults && (
                      <div
                        className={`absolute left-6 top-[5.5rem] z-20 my-[12px] h-[624px] max-h-[624px] w-[361px] overflow-y-auto rounded-md bg-primarybg bg-opacity-80 p-[6px]`}
                      >
                        {!loadingVendorLocations ? (
                          <div
                            className="h-full overflow-y-auto"
                            onScroll={handleListScroll}
                          >
                            {vendorLocations.map((loc, index) => (
                              <VendorLocationRow
                                key={loc?.location_id ?? index}
                                index={index}
                                data={{
                                  filteredVendorLocations: vendorLocations,
                                  selectedVendorLocation,
                                  setSelectedVendorLocation,
                                  distances,
                                  sanitizeUrl,
                                  sanitizePhoneNumber,
                                  hoursOfOperationStringify,
                                }}
                              />
                            ))}
                            {loadingMoreCards && (
                              <div className="flex w-full justify-center py-3">
                                <ClipLoader
                                  color="#1D213B"
                                  size={28}
                                  loading
                                  aria-label="Loading more vendors"
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <ClipLoader
                              color="#1D213B"
                              loading={loadingVendorLocations}
                              size={60}
                              aria-label="Loading Spinner"
                              data-testid="loader"
                              className="flex h-full w-full justify-center"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {!loadingVendorLocations && (
                      <VendorFilterButtons
                        setFilterTires={updateFilterTires}
                        setFilterRental={updateFilterRental}
                        setFilterTowing={updateFilterTowing}
                        setFilterGasStation={updateFilterGasStation}
                        setFilterServiceCenter={updateFilterServiceCenter}
                        filterTires={filterTires}
                        filterGasStation={filterGasStation}
                        filterRental={filterRental}
                        filterTowing={filterTowing}
                        filterServiceCenter={filterServiceCenter}
                        selectedRadius={selectedRadius}
                        setSelectedRadius={setSelectedRadius}
                        openRadiusSelect={openRadiusSelect}
                        setOpenRadiusSelect={setOpenRadiusSelect}
                        toggleOpenRadiusSelect={toggleOpenRadiusSelect}
                        isCompassExpandable={isActive}
                      />
                    )}

                    {selectedVendorLocation &&
                      selectedVendorLocation?.location_id?.length > 0 &&
                      !loadingVendorLocations && (
                        <>
                          <div
                            className="fixed inset-0 z-40 bg-black bg-opacity-50"
                            onClick={() => setSelectedVendorLocation(null)} // Clicking outside the modal closes it
                          ></div>
                          <SelectedLocationDetails
                            distances={distances}
                            openDirectionsWindow={openDirectionsWindow}
                            selectedVendorLocation={selectedVendorLocation}
                            setSelectedVendorLocation={
                              updateSelectedVendorLocation
                            }
                          />
                        </>
                      )}

                    {/* Hidden behind the details modal: these share the
                        scrim's z-40 and come after it, so they would otherwise
                        float on top of the dimmed backdrop. */}
                    {!selectedVendorLocation && canSearchThisArea && (
                      <SearchThisAreaButton
                        onClick={handleSearchThisArea}
                        isSearching={loadingVendorLocations}
                        isPreActive={isPreActive}
                      />
                    )}

                    {!selectedVendorLocation && (
                      <RecenterButton onClick={handleRecenter} />
                    )}

                    <Map
                      mapTypeControl={false}
                      streetViewControl={false}
                      fullscreenControl={false}
                      defaultCenter={{
                        lat: userLocation.lat,
                        lng: userLocation.lng,
                      }}
                      defaultZoom={10}
                      styles={mapStyles}
                      onIdle={handleMapIdle}
                      onDragstart={handleMapDragStart}
                    >
                      {isLoaded && searchCenter !== null && (
                        <Circle
                          center={{
                            lat: searchCenter.lat,
                            lng: searchCenter.lng,
                          }}
                          radius={selectedRadius * 1609.34} // Convert miles to meters
                          strokeColor={'#0c4cb3'}
                          strokeOpacity={0.8}
                          strokeWeight={3}
                          fillColor={'#3b82f6'}
                          fillOpacity={0.15}
                        />
                      )}
                      {isLoaded &&
                        markerPoints.map((point) => (
                          <Marker
                            key={point.id}
                            position={{
                              lat: point.position[1],
                              lng: point.position[0],
                            }}
                            onClick={() => handleMarkerClick(point.id)}
                            icon={(() => {
                              // Special handling for Penske and Ryder to use actual logo files
                              if (point.name === 'Penske Truck Rental') {
                                return {
                                  url: '/penske-p.png',
                                  scaledSize: new google.maps.Size(50, 50),
                                  anchor: new google.maps.Point(25, 50),
                                };
                              } else if (point.name === 'Ryder Truck Rental') {
                                return {
                                  url: '/ryder-r.svg',
                                  scaledSize: new google.maps.Size(50, 50),
                                  anchor: new google.maps.Point(25, 50),
                                };
                              } else {
                                // Default marker for other vendors
                                return {
                                  url: generateCustomMarkerIcon(
                                    point.icon,
                                    renderIconBg(point.primary_vendor_type),
                                    point.name
                                  ),
                                  scaledSize: new google.maps.Size(40, 40),
                                };
                              }
                            })()}
                          />
                        ))}
                    </Map>
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={loadingVendorLocations}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full justify-center"
                    />
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowSearchResults(!showSearchResults)}
                className="absolute bottom-4 left-4 z-40 flex flex-row items-center justify-center space-x-1 rounded-full bg-accent p-2"
              >
                {!showSearchResults ? (
                  <ListBullets size={24} />
                ) : (
                  <X size={24} />
                )}{' '}
                <p>
                  {showSearchResults
                    ? 'Hide Search Results'
                    : 'Show Search Results'}
                </p>
              </button>
            </div>
          </APIProvider>
        );
      case 'mobile':
        return (
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <div className="flex h-full w-full flex-col">
              <div className="relative h-full w-full">
                {isLoaded && userLocation !== null ? (
                  <>
                    {!selectedVendorLocation && (
                      <div
                        className={`absolute left-0 top-0 z-40 flex h-[140px] max-h-[140px] w-full flex-col gap-3 bg-primarybg p-3`}
                      >
                        <div className="flex w-full flex-row items-center justify-between space-x-2 align-middle">
                          <div className="relative flex w-full items-center rounded-full border-[1px] border-bordergray bg-white p-3">
                            <MagnifyingGlass size={24} />
                            <input
                              type="text"
                              className="ml-2 w-full border-none outline-none"
                              placeholder="Search..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                              <XCircle
                                size={24}
                                className="cursor-pointer"
                                onClick={() => setSearchQuery('')}
                              />
                            )}
                          </div>
                          {(userRecord as CustomerWithDriverOnboarding)
                            ?.onboardingSteps?.direct_deposit_verified && (
                            <button
                              onClick={() => setIsSupportModalOpen(true)}
                              disabled={isPreActive}
                              className="z-40 flex h-[50px] min-h-[50px] min-w-[180px] flex-row items-center justify-center space-x-1 rounded-full border-[1px] border-danger bg-white p-1 text-danger hover:cursor-pointer hover:bg-red-200 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <PhoneOutgoing size={20} weight="bold" />
                              <span className="text-[14px] font-[600]">
                                Get Emergency Help
                              </span>
                              <SupportModal
                                isOpen={isSupportModalOpen}
                                isFleetNet
                                onClose={() => setIsSupportModalOpen(false)}
                              />
                            </button>
                          )}
                        </div>
                        <VendorFilterButtons
                          setFilterTires={updateFilterTires}
                          setFilterRental={updateFilterRental}
                          setFilterTowing={updateFilterTowing}
                          setFilterGasStation={updateFilterGasStation}
                          setFilterServiceCenter={updateFilterServiceCenter}
                          filterTires={filterTires}
                          filterGasStation={filterGasStation}
                          filterRental={filterRental}
                          filterTowing={filterTowing}
                          filterServiceCenter={filterServiceCenter}
                          selectedRadius={selectedRadius}
                          setSelectedRadius={setSelectedRadius}
                          openRadiusSelect={openRadiusSelect}
                          setOpenRadiusSelect={setOpenRadiusSelect}
                          toggleOpenRadiusSelect={toggleOpenRadiusSelect}
                          isCompassExpandable={isActive}
                        />
                      </div>
                    )}
                    {!selectedVendorLocation && (
                      <DraggableSearchResults
                        filteredVendorLocations={vendorLocations}
                        setSelectedVendorLocation={setSelectedVendorLocation}
                        selectedVendorLocation={selectedVendorLocation}
                        distances={distances}
                        hoursOfOperationStringify={hoursOfOperationStringify}
                        loadingVendorLocations={loadingVendorLocations}
                        hasMore={hasMoreCards}
                        loadingMore={loadingMoreCards}
                        onLoadMore={() =>
                          fetchVendorCards(userId ?? '', cardOffset, true)
                        }
                      />
                    )}

                    {selectedVendorLocation &&
                      selectedVendorLocation?.location_id?.length > 0 && (
                        <>
                          <div
                            className="fixed inset-0 z-40 bg-black bg-opacity-50"
                            onClick={() => setSelectedVendorLocation(null)} // Clicking outside the modal closes it
                          ></div>
                          <SelectedLocationDetails
                            distances={distances}
                            openDirectionsWindow={openDirectionsWindow}
                            selectedVendorLocation={selectedVendorLocation}
                            setSelectedVendorLocation={
                              updateSelectedVendorLocation
                            }
                          />
                        </>
                      )}

                    {/* Hidden behind the details modal: these share the
                        scrim's z-40 and come after it, so they would otherwise
                        float on top of the dimmed backdrop — and the 140px
                        header their offset is measured against is gone in that
                        state. */}
                    {!selectedVendorLocation && canSearchThisArea && (
                      <SearchThisAreaButton
                        onClick={handleSearchThisArea}
                        isSearching={loadingVendorLocations}
                        isPreActive={isPreActive}
                      />
                    )}

                    {!selectedVendorLocation && (
                      <RecenterButton onClick={handleRecenter} />
                    )}

                    <Map
                      mapTypeControl={false}
                      streetViewControl={false}
                      fullscreenControl={false}
                      defaultCenter={{
                        lat: userLocation.lat,
                        lng: userLocation.lng,
                      }}
                      defaultZoom={10}
                      styles={mapStyles}
                      onIdle={handleMapIdle}
                      onDragstart={handleMapDragStart}
                    >
                      {isLoaded && searchCenter !== null && (
                        <Circle
                          center={{
                            lat: searchCenter.lat,
                            lng: searchCenter.lng,
                          }}
                          radius={selectedRadius * 1609.34} // Convert miles to meters
                          strokeColor={'#0c4cb3'}
                          strokeOpacity={0.8}
                          strokeWeight={3}
                          fillColor={'#3b82f6'}
                          fillOpacity={0.15}
                        />
                      )}
                      {isLoaded &&
                        markerPoints.map((point) => (
                          <Marker
                            key={point.id}
                            position={{
                              lat: point.position[1],
                              lng: point.position[0],
                            }}
                            onClick={() => handleMarkerClick(point.id)}
                            icon={(() => {
                              // Special handling for Penske and Ryder to use actual logo files
                              if (point.name === 'Penske Truck Rental') {
                                return {
                                  url: '/penske-p.png',
                                  scaledSize: new google.maps.Size(50, 50),
                                  anchor: new google.maps.Point(25, 50),
                                };
                              } else if (point.name === 'Ryder Truck Rental') {
                                return {
                                  url: '/ryder-r.svg',
                                  scaledSize: new google.maps.Size(50, 50),
                                  anchor: new google.maps.Point(25, 50),
                                };
                              } else {
                                // Default marker for other vendors
                                return {
                                  url: generateCustomMarkerIcon(
                                    point.icon,
                                    renderIconBg(point.primary_vendor_type),
                                    point.name
                                  ),
                                  scaledSize: new google.maps.Size(40, 40),
                                };
                              }
                            })()}
                          />
                        ))}
                    </Map>
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={loadingVendorLocations}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full justify-center"
                    />
                  </div>
                )}
              </div>
            </div>
          </APIProvider>
        );
    }
  };

  const VendorLocationRow = React.memo(
    ({ index, data }: { index: number; data: any }) => {
      const {
        filteredVendorLocations,
        selectedVendorLocation,
        setSelectedVendorLocation,
        distances,
        sanitizeUrl,
        sanitizePhoneNumber,
        hoursOfOperationStringify,
      } = data;
      const vendorLocation = filteredVendorLocations[index];

      return (
        <div className="px-2 py-1">
          <div
            key={vendorLocation?.location_id}
            onClick={() => {
              if (
                vendorLocation?.location_id ===
                selectedVendorLocation?.location_id
              ) {
                setSelectedVendorLocation(null);
              } else {
                setSelectedVendorLocation(vendorLocation);
              }
            }}
            className={`relative flex max-w-[338px] flex-col gap-y-2 rounded-lg bg-white p-4 hover:cursor-pointer hover:bg-gray-50 ${
              selectedVendorLocation?.location_id ===
              vendorLocation?.location_id
                ? 'border-[1px] border-accent'
                : 'border-[1px] border-bordergray'
            }`}
          >
            <div className="flex justify-between">
              <div className="flex flex-col">
                <h2 className="max-w-[200px] text-wrap text-[16px] font-[600] text-primary">
                  {vendorLocation?.vendor?.business_name}
                </h2>
                {vendorLocation?.location_name !==
                  vendorLocation?.vendor?.business_name && (
                  <h2 className="max-w-[200px] text-wrap text-[14px] font-[600] text-graytertiary">
                    {vendorLocation?.location_name}
                  </h2>
                )}
              </div>
              <div className="flex flex-row items-center space-x-2 text-[12px] text-gray-500">
                <div className="flex flex-row items-center space-x-[2px] rounded-[24px] border-[1px] border-bordergray bg-[#F4F5F6] px-[4px] py-[3px] align-middle">
                  <MapPin size={16} />
                  <span>{distances[vendorLocation?.location_id]}</span>
                </div>
              </div>
            </div>
            {vendorLocation?.vendor?.is_highly_recommended && (
              <div className="flex w-full justify-end">
                <div className="flex max-w-[120px] items-center justify-center space-x-[2px] rounded-full border-[1px] border-accent bg-yellow-50 px-[4px] py-[1px] text-[12px] text-accent">
                  <Sparkle size={16} />
                  <p>Recommended</p>
                </div>
              </div>
            )}
            {vendorLocation?.vendor?.is_not_recommended && (
              <div className="flex w-full justify-end">
                <div className="flex max-w-[150px] items-center justify-center space-x-[2px] rounded-full border-[1px] border-danger bg-red-50 px-[4px] py-[1px] text-[12px] text-danger">
                  <Warning size={16} />
                  <p>Not Recommended</p>
                </div>
              </div>
            )}
            {vendorLocation?.description_of_services && (
              <div className="h-[20px] max-h-[20px] w-[321px] max-w-[300px] overflow-ellipsis">
                <p className="line-clamp-1 text-sm text-gray-500">
                  {vendorLocation?.description_of_services}
                </p>
              </div>
            )}
            <div className="flex w-full flex-row items-center justify-between align-middle">
              {vendorLocation?.hours_of_operation && (
                <div className="flex w-[60%] max-w-[60%] flex-row items-center space-x-2 text-sm text-gray-600">
                  <Clock
                    size={16}
                    className="w-[16px] min-w-[16px] text-primary opacity-50"
                  />
                  <span className="line-clamp-2 max-w-[90%] overflow-ellipsis">
                    {hoursOfOperationStringify(
                      vendorLocation?.hours_of_operation ?? ''
                    )}
                  </span>
                </div>
              )}
              {vendorLocation?.deals && vendorLocation?.deals?.length > 0 && (
                <div className="flex items-center space-x-2 rounded-[24px] border-[1px] border-bordergray bg-[#E7F8F0] px-2 py-1">
                  {/*<Promotion className="text-green-500" size={20} />*/}
                  <span className="flex flex-row items-center space-x-1 align-middle text-sm text-[#037355]">
                    <Ticket size={20} /> <p>Promotion</p>
                  </span>
                </div>
              )}
            </div>
            {vendorLocation?.website_url && (
              <div className="flex flex-row items-center space-x-2 align-middle">
                <Globe size={16} className="text-primary opacity-50" />
                <a
                  href={sanitizeUrl(vendorLocation?.website_url)}
                  className="line-clamp-1 w-[74%] overflow-ellipsis text-[14px] text-primary underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vendorLocation?.website_url}
                </a>
              </div>
            )}
            <div className="relative flex w-full flex-row items-center justify-between align-middle">
              <div className="flex w-[65%] flex-row items-center space-x-2 align-middle">
                <Phone size={16} className="text-primary opacity-50" />
                <a
                  href={`tel:${sanitizePhoneNumber(vendorLocation?.business_phone_number)}`}
                  className="text-[14px] text-primary underline"
                >
                  {vendorLocation?.business_phone_number}
                </a>
              </div>
              {vendorLocation?.vendor?.is_highly_recommended && (
                <img
                  src="/TACH Certified Vendor.png"
                  alt="TACH Certified Vendor"
                  className="absolute bottom-0 right-0 h-14 w-auto object-contain"
                />
              )}
            </div>
          </div>
        </div>
      );
    }
  );

  return <>{renderScreenView(screenView)}</>;
}

export default VendorMarketplace;

interface ParsedHoursOfOperation {
  isOpen: boolean;
  from: string | dayjs.Dayjs;
  to: string | dayjs.Dayjs;
}

export const hoursOfOperationStringify = (hoursOfOperation: string) => {
  return Object.entries(
    JSON.parse(hoursOfOperation) as {
      Monday: ParsedHoursOfOperation;
      Tuesday: ParsedHoursOfOperation;
      Wednesday: ParsedHoursOfOperation;
      Thursday: ParsedHoursOfOperation;
      Friday: ParsedHoursOfOperation;
      Saturday: ParsedHoursOfOperation;
      Sunday: ParsedHoursOfOperation;
    }
  )
    .map(([day, details]) => {
      // Ensure `from` and `to` are strings; if they are Dayjs objects, convert to string
      const from =
        typeof details.from === 'string'
          ? details.from
          : details.from.format('hh:mm A');
      const to =
        typeof details.to === 'string'
          ? details.to
          : details.to.format('hh:mm A');

      if (details.isOpen) {
        return `${day.slice(0, 3)}: ${from} to ${to}`;
      } else {
        return `${day.slice(0, 3)}: Closed`;
      }
    })
    .join(', ');
};

export const renderIconBg = (vendor_type: string) => {
  switch (vendor_type) {
    case 'repair_shop':
      return '#E95230';
    case 'fuel_station':
      return '#E99730';
    case 'towing_service':
      return '#06b6d4';
    case 'truck_rental':
      return '#84cc16';
    case 'tire_supplier':
      return '#6366f1';
    case 'preventative_maintenance':
      return '#06b6d4';
    case 'other':
      return '#1F81DC';
    default:
      return '#1F81DC';
  }
};
